// Business Accounts — Super Admin control panel.
// Summary cards + searchable/filterable business table + slide-in detail drawer
// with company/owner/subscription/usage, admin actions (suspend/activate/extend),
// Login-as-Business impersonation, and a per-business audit log.
// Route: /super-admin/businesses  (super_admin only — enforced server-side).

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Building2, Search, Users, Ban, CheckCircle2, LogIn, CalendarPlus, X, Loader2,
  IndianRupee, TrendingUp, Clock, AlertTriangle, Sparkles, ShieldCheck, History,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN");
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_BADGE = {
  active:    "bg-emerald-100 text-emerald-700",
  suspended: "bg-rose-100 text-rose-700",
  trial:     "bg-amber-100 text-amber-700",
  expired:   "bg-slate-200 text-slate-600",
  free:      "bg-slate-100 text-slate-600",
};

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${tint}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{value}</div>
      <div className="text-[12px] text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}

export default function BusinessAccounts() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [detail, setDetail] = useState(null);       // business_id currently open in drawer
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);      // { action, biz, days? }
  const [busy, setBusy] = useState(false);

  const fetchSummary = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/summary`, { credentials: "include" });
      if (r.ok) setSummary(await r.json());
    } catch (_) {}
  };
  const fetchList = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set("search", search);
      const r = await fetch(`${BACKEND_URL}/api/super-admin/businesses?${qs}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setRows(Array.isArray(d) ? d : (d.businesses || []));
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, []);
  useEffect(() => { const t = setTimeout(fetchList, 300); return () => clearTimeout(t); }, [search]);

  const industries = useMemo(() => Array.from(new Set(rows.map(r => r.industry).filter(Boolean))), [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    if (statusFilter !== "all") {
      list = list.filter(r => (r.status === statusFilter) || (r.active_subscription?.status === statusFilter));
    }
    if (planFilter !== "all") {
      list = list.filter(r => (r.active_subscription?.plan_name || "Free").toLowerCase() === planFilter.toLowerCase());
    }
    return list;
  }, [rows, statusFilter, planFilter]);

  const openDetail = async (biz) => {
    setDetail(biz.business_id);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/businesses/${biz.business_id}`, { credentials: "include" });
      if (r.ok) setDetailData(await r.json());
    } finally { setDetailLoading(false); }
  };

  const runStatus = async (biz, next) => {
    setBusy(true);
    try {
      const verb = next === "suspended" ? "suspend" : "activate";
      const r = await fetch(`${BACKEND_URL}/api/super-admin/businesses/${biz.business_id}/${verb}`, {
        method: "PATCH", credentials: "include",
      });
      if (r.ok) { toast.success(`Business ${next}`); fetchList(); fetchSummary(); if (detail === biz.business_id) openDetail(biz); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Failed"); }
    } finally { setBusy(false); setConfirm(null); }
  };

  const runExtend = async (biz, days) => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/businesses/${biz.business_id}/extend`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days }),
      });
      if (r.ok) { const d = await r.json(); toast.success(`Extended to ${fmtDate(d.new_expiry)}`); fetchSummary(); if (detail === biz.business_id) openDetail(biz); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Extend failed"); }
    } finally { setBusy(false); setConfirm(null); }
  };

  const runImpersonate = async (biz) => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/impersonate`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_id: biz.business_id }),
      });
      if (r.ok) {
        toast.success(`Logging in as ${biz.name}…`);
        try { localStorage.removeItem("selected_business"); } catch (_) {}
        setTimeout(() => { window.location.href = "/dashboard"; }, 500);
      } else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Impersonation failed"); }
    } finally { setBusy(false); setConfirm(null); }
  };

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-10" data-testid="business-accounts-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-6 h-6 text-indigo-600" /> Business Accounts
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Manage every business from one place.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat icon={Building2} label="Total Businesses" value={summary?.total ?? "—"} tint="bg-indigo-50 text-indigo-600" />
        <Stat icon={CheckCircle2} label="Active" value={summary?.active ?? "—"} tint="bg-emerald-50 text-emerald-600" />
        <Stat icon={Sparkles} label="Trial Accounts" value={summary?.trial ?? "—"} tint="bg-amber-50 text-amber-600" />
        <Stat icon={ShieldCheck} label="Paid Subscriptions" value={summary?.paid_subscriptions ?? "—"} tint="bg-violet-50 text-violet-600" />
        <Stat icon={Ban} label="Suspended" value={summary?.suspended ?? "—"} tint="bg-rose-50 text-rose-600" />
        <Stat icon={Clock} label="Expired" value={summary?.expired ?? "—"} tint="bg-slate-100 text-slate-500" />
        <Stat icon={TrendingUp} label="New (30 days)" value={summary?.new_last_30d ?? "—"} tint="bg-sky-50 text-sky-600" />
        <Stat icon={IndianRupee} label="Total Revenue" value={summary ? fmtINR(summary.total_revenue) : "—"} tint="bg-emerald-50 text-emerald-600" />
        <Stat icon={TrendingUp} label="MRR" value={summary ? fmtINR(summary.mrr) : "—"} tint="bg-indigo-50 text-indigo-600" />
        <Stat icon={AlertTriangle} label="Expiring in 7 days" value={summary?.expiring_7d ?? "—"} tint="bg-amber-50 text-amber-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-1.5 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, owner, email, phone, city…"
            className="bg-transparent outline-none text-sm w-full" data-testid="ba-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="Free">Free</SelectItem>
            <SelectItem value="Starter">Starter</SelectItem>
            <SelectItem value="Growth">Growth</SelectItem>
            <SelectItem value="Enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto">{filtered.length} of {rows.length}</div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-[11px] uppercase tracking-wide text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">Business</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-right px-4 py-3">Users</th>
                <th className="text-right px-4 py-3">Leads</th>
                <th className="text-left px-4 py-3">Registered</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [0,1,2,3,4].map(i => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td colSpan={8} className="px-4 py-3"><div className="h-6 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No businesses match.</td></tr>
              ) : filtered.map(b => {
                const st = b.status === "suspended" ? "suspended" : (b.active_subscription?.status || "free");
                return (
                  <tr key={b.business_id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-3">
                      <button onClick={() => openDetail(b)} className="flex items-center gap-2.5 text-left" data-testid={`ba-row-${b.business_id}`}>
                        {b.logo_url
                          ? <img src={b.logo_url} alt="" className="w-8 h-8 rounded-lg object-contain bg-slate-50 border border-slate-100" />
                          : <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">{(b.name||"?").charAt(0).toUpperCase()}</div>}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 dark:text-white truncate hover:text-indigo-600">{b.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{b.city || b.industry || b.business_id}</div>
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700 dark:text-slate-300 truncate max-w-[180px]">{b.owner_name || "—"}</div>
                      <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{b.owner_email || ""}</div>
                    </td>
                    <td className="px-4 py-3">{b.active_subscription?.plan_name || "Free"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{b.user_count ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{b.lead_count ?? 0}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(b.created_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[st] || STATUS_BADGE.free}`}>{st.toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" title="Login as business" onClick={() => setConfirm({ action: "impersonate", biz: b })}><LogIn className="w-4 h-4" /></Button>
                      {b.status === "suspended"
                        ? <Button size="sm" variant="ghost" className="text-emerald-600" title="Activate" onClick={() => setConfirm({ action: "activate", biz: b })}><CheckCircle2 className="w-4 h-4" /></Button>
                        : <Button size="sm" variant="ghost" className="text-rose-600" title="Suspend" onClick={() => setConfirm({ action: "suspend", biz: b })}><Ban className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="ghost" title="Extend 30 days" onClick={() => setConfirm({ action: "extend", biz: b, days: 30 })}><CalendarPlus className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(15,23,42,.4)" }} onClick={() => setDetail(null)}>
          <div className="w-full max-w-xl bg-white dark:bg-slate-900 h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="ba-detail-drawer">
            <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-5 py-4 flex items-center justify-between">
              <h2 className="font-bold text-slate-900 dark:text-white">Business Details</h2>
              <button onClick={() => setDetail(null)} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            {detailLoading || !detailData ? (
              <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <div className="p-5 space-y-6">
                {/* Company */}
                <Section title="Company">
                  <Field label="Name" value={detailData.business?.name} />
                  <Field label="Business ID" value={detailData.business?.business_id} mono />
                  <Field label="Industry" value={detailData.business?.industry} />
                  <Field label="City / State" value={[detailData.business?.city, detailData.business?.state].filter(Boolean).join(", ")} />
                  <Field label="GST" value={detailData.business?.gstin || detailData.business?.gst_number} />
                  <Field label="Registered" value={fmtDate(detailData.business?.created_at)} />
                  <Field label="Status" value={(detailData.business?.status || "active").toUpperCase()} />
                </Section>
                {/* Subscription */}
                {detailData.subscriptions?.[0] && (
                  <Section title="Subscription">
                    <Field label="Plan" value={detailData.subscriptions[0].plan_name || detailData.subscriptions[0].plan_id} />
                    <Field label="Status" value={(detailData.subscriptions[0].status || "").toUpperCase()} />
                    <Field label="Billing" value={detailData.subscriptions[0].billing_cycle || "—"} />
                    <Field label="Expiry" value={fmtDate(detailData.subscriptions[0].expiry_date)} />
                  </Section>
                )}
                {/* Usage */}
                <Section title="Usage">
                  <div className="grid grid-cols-3 gap-2 col-span-2">
                    {Object.entries(detailData.usage || {}).map(([k, v]) => (
                      <div key={k} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-2.5 text-center">
                        <div className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{v}</div>
                        <div className="text-[10px] text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
                      </div>
                    ))}
                  </div>
                </Section>
                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setConfirm({ action: "impersonate", biz: detailData.business })} className="bg-indigo-600 text-white"><LogIn className="w-4 h-4 mr-1" /> Login as Business</Button>
                  {detailData.business?.status === "suspended"
                    ? <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => setConfirm({ action: "activate", biz: detailData.business })}><CheckCircle2 className="w-4 h-4 mr-1" /> Activate</Button>
                    : <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setConfirm({ action: "suspend", biz: detailData.business })}><Ban className="w-4 h-4 mr-1" /> Suspend</Button>}
                  <Button size="sm" variant="outline" onClick={() => setConfirm({ action: "extend", biz: detailData.business, days: 30 })}><CalendarPlus className="w-4 h-4 mr-1" /> Extend 30d</Button>
                </div>
                {/* Audit */}
                {detailData.audit?.length > 0 && (
                  <Section title="Recent admin actions">
                    <div className="col-span-2 space-y-1.5">
                      {detailData.audit.map(l => (
                        <div key={l.log_id} className="flex items-center gap-2 text-[12px] text-slate-600 dark:text-slate-400">
                          <History className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="font-semibold text-slate-800 dark:text-slate-200">{l.action.replace(/_/g, " ")}</span>
                          <span className="text-slate-400">by {l.performed_by_email || l.performed_by}</span>
                          <span className="ml-auto text-slate-400">{fmtDateTime(l.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === "impersonate" && `Login as ${confirm?.biz?.name}?`}
              {confirm?.action === "suspend" && `Suspend ${confirm?.biz?.name}?`}
              {confirm?.action === "activate" && `Activate ${confirm?.biz?.name}?`}
              {confirm?.action === "extend" && `Extend ${confirm?.biz?.name} by 30 days?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "impersonate" && "You'll be logged into this business's account as its admin. A banner will let you return to Super Admin any time. This is recorded in the audit log."}
              {confirm?.action === "suspend" && "The business's users will lose access until reactivated. This is recorded in the audit log."}
              {confirm?.action === "activate" && "Restores access for this business's users."}
              {confirm?.action === "extend" && "Adds 30 days to the current subscription expiry."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() => {
                if (!confirm) return;
                if (confirm.action === "impersonate") runImpersonate(confirm.biz);
                else if (confirm.action === "suspend") runStatus(confirm.biz, "suspended");
                else if (confirm.action === "activate") runStatus(confirm.biz, "active");
                else if (confirm.action === "extend") runExtend(confirm.biz, confirm.days || 30);
              }}
              className={confirm?.action === "suspend" ? "bg-rose-600 text-white" : "bg-indigo-600 text-white"}
              data-testid="ba-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">{children}</div>
    </div>
  );
}
function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className={`text-[13px] text-slate-800 dark:text-slate-200 ${mono ? "font-mono text-[11px]" : ""}`}>{value || "—"}</div>
    </div>
  );
}
